import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { computed } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { Notification, NotificationInboxService } from '../services/notification-inbox.service';

interface NotificationInboxState {
  notifications: Notification[];
  unreadCount: number;
  panelOpen: boolean;
  loading: boolean;
}

function filterByCalendarDay(items: Notification[], minDaysAgo: number, maxDaysAgo: number): Notification[] {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return items.filter(n => {
    const d = new Date(n.created_at);
    d.setHours(0, 0, 0, 0);
    const diffDays = Math.round((today.getTime() - d.getTime()) / 86_400_000);
    return diffDays >= minDaysAgo && diffDays <= maxDaysAgo;
  });
}


export const TASK_LIST_EVENT_TYPES = new Set(['new_task_assigned', 'task_created', 'task_reassigned', 'overdue_task_self']);

export interface ResolvedNotificationRoute {
  commands: string[];
  queryParams?: Record<string, string>;
}

export interface NotificationRouteContext {
  task_code?: string;
  workflow_code?: string;
  branch_number?: string;
  damage_status?: string;
}

// Shared by the inbox panel (navigateTo, below) and the live toast popup
// (app.component.ts), so a notification deep-links to the specific
// task/incident the same way either path is reached.
export function resolveNotificationRoute(
  eventType: string,
  referenceId: string,
  context?: NotificationRouteContext | null,
): ResolvedNotificationRoute {
  if (TASK_LIST_EVENT_TYPES.has(eventType)) {
    const taskCode = context?.task_code;
    if (taskCode) {
      return {
        commands: ['/tasks'],
        queryParams: { task_code: taskCode, workflow_code: context?.workflow_code ?? referenceId },
      };
    }
    return { commands: ['/tasks'] };
  }

  const queryParams: Record<string, string> = {};
  if (context?.damage_status) queryParams['damage_status'] = context.damage_status;
  if (context?.branch_number) queryParams['branch_number'] = context.branch_number;

  return Object.keys(queryParams).length
    ? { commands: ['/incidents', referenceId], queryParams }
    : { commands: ['/incidents', referenceId] };
}

export const NotificationInboxStore = signalStore(
  { providedIn: 'root' },
  withState<NotificationInboxState>({
    notifications: [],
    unreadCount: 0,
    panelOpen: false,
    loading: false,
  }),
  withComputed((store) => ({
    today: computed(() => filterByCalendarDay(store.notifications(), 0, 0)),
    yesterday: computed(() => filterByCalendarDay(store.notifications(), 1, 1)),
    last5Days: computed(() => filterByCalendarDay(store.notifications(), 2, 6)),
  })),
  withMethods((store, svc = inject(NotificationInboxService), router = inject(Router)) => {
    // Guards against two concurrent fetches racing: only the response matching
    // the latest-issued fetchToken is applied; an older one that resolves late is dropped.
    let fetchToken = 0;
    // Guards against two concurrent mark-as-read calls for the same notification
    // (e.g. a double-click) both reading "unread" and both decrementing the count.
    const pendingReads = new Set<string>();

    return {
      async loadNotifications() {
        const token = ++fetchToken;
        patchState(store, { loading: true });
        try {
          const data = await firstValueFrom(svc.getNotifications({ limit: 100 }));
          if (token !== fetchToken) return;
          patchState(store, { notifications: data.notifications ?? [], loading: false });
        } catch {
          if (token !== fetchToken) return;
          patchState(store, { loading: false });
        }
      },

      async pollUnreadCount() {
        try {
          const { unread_count } = await firstValueFrom(svc.getUnreadCount());
          patchState(store, { unreadCount: unread_count ?? 0 });
        } catch { /* badge stays at last known value */ }
      },

      // Called when a browser_push event arrives over Pusher. Bumps the badge
      // immediately from the event itself instead of re-polling the backend,
      // since the backend's inbox write (which pollUnreadCount would read) can
      // still be in flight when the push fires — polling here would race it
      // and often show a stale count.
      handlePushNotification() {
        patchState(store, { unreadCount: store.unreadCount() + 1 });
        if (store.panelOpen()) {
          const token = ++fetchToken;
          firstValueFrom(svc.getNotifications({ limit: 100 }))
            .then(data => {
              if (token !== fetchToken) return;
              patchState(store, { notifications: data.notifications ?? [] });
            })
            .catch(() => { /* list stays stale until next panel open; count is already correct */ });
        }
      },

      async togglePanel() {
        const opening = !store.panelOpen();
        patchState(store, { panelOpen: opening });
        if (opening) {
          const token = ++fetchToken;
          patchState(store, { loading: true });
          try {
            const data = await firstValueFrom(svc.getNotifications({ limit: 100 }));
            if (token !== fetchToken) return;
            patchState(store, {
              notifications: data.notifications ?? [],
              unreadCount: data.unread_count ?? store.unreadCount(),
              loading: false,
            });
          } catch {
            if (token !== fetchToken) return;
            patchState(store, { loading: false });
          }
        }
      },

      closePanel() {
        if (store.panelOpen()) {
          patchState(store, { panelOpen: false });
        }
      },

      async markRead(id: string) {
        if (pendingReads.has(id)) return;
        pendingReads.add(id);
        try {
          const wasUnread = (store.notifications() ?? []).find(n => n.notification_id === id)?.is_read === false;
          await firstValueFrom(svc.markAsRead(id));
          patchState(store, {
            notifications: (store.notifications() ?? []).map(n =>
              n.notification_id === id ? { ...n, is_read: true } : n
            ),
            unreadCount: wasUnread ? Math.max(0, store.unreadCount() - 1) : store.unreadCount(),
          });
        } catch {
          /* request failed — leave state as-is, matches server truth */
        } finally {
          pendingReads.delete(id);
        }
      },

      async markAllRead() {
        const idsToMark = new Set(
          (store.notifications() ?? []).filter(n => !n.is_read).map(n => n.notification_id)
        );
        if (idsToMark.size === 0) return;
        try {
          await firstValueFrom(svc.markAllAsRead());
        } catch {
          return; // request failed — leave state as-is, matches server truth
        }
        patchState(store, {
          notifications: (store.notifications() ?? []).map(n =>
            idsToMark.has(n.notification_id) ? { ...n, is_read: true } : n
          ),
          unreadCount: Math.max(0, store.unreadCount() - idsToMark.size),
        });
      },

      async navigateTo(n: Notification) {
        const id = n.notification_id;
        if (!pendingReads.has(id)) {
          pendingReads.add(id);
          const wasUnread = !n.is_read;
          try {
            await firstValueFrom(svc.markAsRead(id));
            patchState(store, {
              notifications: (store.notifications() ?? []).map(item =>
                item.notification_id === id ? { ...item, is_read: true } : item
              ),
              unreadCount: wasUnread ? Math.max(0, store.unreadCount() - 1) : store.unreadCount(),
            });
          } catch {
            /* mark-as-read failed; still navigate, but don't claim it's read */
          } finally {
            pendingReads.delete(id);
          }
        }
        patchState(store, { panelOpen: false });
        const { commands, queryParams } = resolveNotificationRoute(n.event_type, n.reference_id, {
          task_code: n.payload?.task_code,
          workflow_code: n.payload?.workflow_code,
          branch_number: n.branch_number,
          damage_status: n.damage_status,
        });
        await router.navigate(commands, queryParams ? { queryParams } : undefined);
      },
    };
  })
);
